import { type MetaFunction, Outlet } from "@remix-run/react";
import { useEffect } from "react";

import { companyInformationQueryOptions } from "~/api/queries/useCompanyInformation";
import { queryClient } from "~/api/queryClient";
import { initLandingSound } from "~/modules/Landing/lib/landingSound";
import { serverFetchSafe } from "~/utils/server-fetch.server";

import type { GetCompanyInformationResponse } from "~/api/generated-api";

/**
 * ═══ POR QUÉ ESTE `loader` ES EL QUE DESBLOQUEA EL SSR DE TODAS LAS LANDINGS ═══
 *
 * Este layout envuelve el 100% de las URLs de la app (`routes.ts`: landings, auth,
 * library, course, admin). Mientras exportaba SÓLO `clientLoader`, la cadena en SSR era:
 *
 *  1. `RemixServer` borra la `loaderData` de toda ruta con `clientLoader` y sin `loader`
 *     de servidor (`@remix-run/react/dist/server.js`, `shouldHydrateRouteLoader`).
 *  2. Pero el router estático igual le deja un `loader: () => null` a esa misma ruta,
 *     porque tiene `hasClientLoader` (`dist/routes.js`, `createServerRoutes`).
 *  3. React Router ve "ruta con loader y data === undefined", corta la hidratación
 *     parcial y sube al `HydrateFallback` más cercano — el `<div />` de `root.tsx`.
 *
 * Resultado medido: TODA landing pública se servía con 13 KB de HTML y ~20 caracteres de
 * texto (el `<title>` y el splash), y cero JSON-LD. Los crawlers de LLM no ejecutan JS,
 * así que recibían una página en blanco.
 *
 * Con `hasLoader === true` la segunda condición del borrado deja de cumplirse (esta ruta
 * no tiene `HydrateFallback` propio) y el árbol renderiza entero hasta la hoja.
 *
 * OJO: si alguna vez se le agrega un `HydrateFallback` a esta ruta o a una landing, el
 * bug vuelve aunque haya `loader`, porque la primera rama de esa condición se cumple sola.
 */

/**
 * Caché en memoria con TTL corto. POR QUÉ: este loader pasa a correr en CADA request de
 * CADA ruta, incluida la mitad autenticada, que antes no hacía ningún fetch de servidor.
 * `company-information` es la misma respuesta para todo el mundo (no depende del usuario),
 * así que cachearla evita sumarle un round-trip a cada navegación dura del admin.
 *
 * LOS FALLOS TAMBIÉN SE CACHEAN, y ese es el punto fino. La primera versión no los guardaba
 * ("así el próximo request reintenta"), que suena prudente y es exactamente al revés: con la
 * API COLGADA —no caída— cada request de CADA URL se come el timeout de 3s entero, incluidas
 * `/admin` y `/library`, que ni usan esta data. Medido contra un socket que acepta y no
 * responde: 3.0s de TTFB en todas, y 6.0s en `/courses/:slug`, que encadena dos fetch.
 * Multiplicado por las requests en vuelo, eso le come el event loop al Node del web.
 *
 * Con caché negativa corta, el primer request paga el timeout y los siguientes salen al
 * instante con el mismo fallback que ya usa la página cuando no hay data. 10s en vez de 60
 * para que la recuperación sea rápida cuando la API vuelve.
 *
 * Con la API CAÍDA de verdad (connection refused) no había problema: falla en milisegundos.
 * El caso malo siempre fue el que no falla rápido.
 */
const COMPANY_INFO_TTL_MS = 60_000;
const COMPANY_INFO_FAILURE_TTL_MS = 10_000;
let companyInfoCache: {
  value: GetCompanyInformationResponse | null;
  expiresAt: number;
} | null = null;

export const loader = async () => {
  const now = Date.now();

  if (companyInfoCache && companyInfoCache.expiresAt > now) {
    return { companyInfo: companyInfoCache.value };
  }

  // `serverFetchSafe` NUNCA tira: si la API está caída devuelve `null`. Es deliberado —
  // si este loader tirara, el `ErrorBoundary` de root se comería la app ENTERA, incluidas
  // las rutas autenticadas que ni dependen de esta llamada.
  const companyInfo = await serverFetchSafe<GetCompanyInformationResponse>(
    "/api/settings/company-information",
  );

  companyInfoCache = {
    value: companyInfo,
    expiresAt: now + (companyInfo ? COMPANY_INFO_TTL_MS : COMPANY_INFO_FAILURE_TTL_MS),
  };

  return { companyInfo };
};

/**
 * Se queda tal cual. En el cliente sigue siendo el que llena la caché de React Query, y
 * con `hasLoader === true` ya no dispara el borrado de data: React Router hidrata con lo
 * que vino del servidor y recién después corre esto. Por `v3_singleFetch`, como no llama
 * a `serverLoader()`, las navegaciones cliente ni piden el `.data` de esta ruta: cero
 * round-trips extra.
 */
export const clientLoader = async () => {
  const companyInfo = await queryClient.ensureQueryData(companyInformationQueryOptions);

  return { companyInfo };
};
clientLoader.hydrate = true as const;

/** Lo que ve un hijo cuando lee `match.data` de esta ruta (lo usa `~/utils/setPageTitle`). */
export type ParentRouteData = Awaited<ReturnType<typeof loader>>;

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const companyShortName = data?.companyInfo?.data?.companyShortName;
  const title = companyShortName ? `${companyShortName}` : "AcademIA";

  return [{ title }];
};

export default function Layout() {
  useEffect(() => {
    initLandingSound();
  }, []);

  return <Outlet />;
}
